import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ChatbotPreview from '../components/ChatbotPreview';

interface ChatbotData {
    id: string;
    name: string;
    appearance: {
        theme: 'light' | 'dark' | 'system';
        primaryColor: string;
    };
}

const ChatbotPreviewPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [chatbot, setChatbot] = useState<ChatbotData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Mock-Daten laden
    useEffect(() => {
        // Hier würdest du normalerweise eine API-Anfrage senden
        setTimeout(() => {
            setChatbot({
                id: id || '1',
                name: 'Kundenservice Bot',
                appearance: {
                    theme: 'light',
                    primaryColor: '#3b82f6',
                },
            });
            setIsLoading(false);
        }, 500);
    }, [id]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!chatbot) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                <p>Chatbot nicht gefunden.</p>
                <Link to="/" className="text-blue-500 hover:underline mt-2 inline-block">
                    Zurück zur Übersicht
                </Link>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Vorschau: {chatbot.name}</h1>
                    <p className="text-gray-600">Hier kannst du sehen, wie dein Chatbot auf einer Website aussehen wird.</p>
                </div>
                <Link
                    to={`/chatbot/${id}`}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                    Zurück zu Einstellungen
                </Link>
            </div>

            <div className="bg-white shadow rounded-lg p-6 mb-8">
                <h2 className="text-xl font-medium text-gray-900 mb-4">Vorschau-Website</h2>
                <p className="text-gray-600 mb-6">
                    Dies ist eine Simulation einer Website, auf der dein Chatbot eingebunden ist. Klicke auf das Chat-Icon unten rechts, um den Chatbot zu öffnen.
                </p>

                {/* Mockup Website */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Website Header */}
                    <div className="bg-gray-800 text-white p-4">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold">Demo Website</h3>
                            <div className="space-x-4">
                                <a href="#" className="text-gray-300 hover:text-white">Home</a>
                                <a href="#" className="text-gray-300 hover:text-white">Produkte</a>
                                <a href="#" className="text-gray-300 hover:text-white">Über uns</a>
                                <a href="#" className="text-gray-300 hover:text-white">Kontakt</a>
                            </div>
                        </div>
                    </div>

                    {/* Website Content */}
                    <div className="p-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Willkommen auf unserer Demo-Seite</h1>
                        <p className="text-gray-600 mb-4">
                            Dies ist eine Beispiel-Website, um zu demonstrieren, wie dein Chatbot in eine echte Website integriert werden würde.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                            <div className="border border-gray-200 rounded-md p-4">
                                <h3 className="font-medium text-lg mb-2">Unsere Produkte</h3>
                                <p className="text-gray-600">
                                    Entdecke unsere neuesten Produkte und Dienstleistungen. Höchste Qualität garantiert!
                                </p>
                            </div>
                            <div className="border border-gray-200 rounded-md p-4">
                                <h3 className="font-medium text-lg mb-2">Kontaktiere uns</h3>
                                <p className="text-gray-600">
                                    Fragen? Probleme? Unser Kundendienst-Team steht bereit, um zu helfen!
                                </p>
                            </div>
                        </div>

                        <div className="mt-8 p-4 bg-gray-50 rounded-md">
                            <h3 className="font-medium text-lg mb-2">Über uns</h3>
                            <p className="text-gray-600">
                                Wir sind ein innovatives Unternehmen, das sich der Bereitstellung erstklassiger Lösungen für unsere Kunden verschrieben hat. Mit jahrelanger Erfahrung und einem engagierten Team sind wir bestrebt, stets die besten Ergebnisse zu liefern.
                            </p>
                        </div>
                    </div>

                    {/* Website Footer */}
                    <div className="bg-gray-100 p-4 border-t border-gray-200">
                        <div className="text-center text-gray-600 text-sm">
                            © 2025 Demo Unternehmen. Alle Rechte vorbehalten.
                        </div>
                    </div>
                </div>
            </div>

            {/* Chatbot Preview Component */}
            <ChatbotPreview
                chatbotName={chatbot.name}
                primaryColor={chatbot.appearance.primaryColor}
                theme={chatbot.appearance.theme}
            />

            <div className="mt-8 bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-medium text-gray-900 mb-4">Tipps für den Test</h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Klicke auf das Chat-Icon unten rechts, um den Chatbot zu öffnen</li>
                    <li>Versuche, diese Testfragen zu stellen:
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Hallo</li>
                            <li>Öffnungszeiten</li>
                            <li>Wie kann ich euch kontaktieren?</li>
                        </ul>
                    </li>
                    <li>In der vollständigen Version wird der Chatbot auf all deine Wissensquellen trainiert sein</li>
                </ul>
            </div>
        </div>
    );
};

export default ChatbotPreviewPage;